// SEO-scalable landing page content. Each "service" landing has rich, keyword-aware
// copy and a city-scalable structure. City context comes from the URL (see App.js).
import { SERVICES, SERVICE_BY_SLUG } from "./services";
import { svgContentTileUrl } from "./propertyPlaceholders";

const tile = (k, l) => svgContentTileUrl(k, l);

// Which slugs are exposed at short SEO-friendly URLs (/sil, /sda, etc.)
export const LANDING_SLUGS = [
  "sil",
  "sda",
  "daily-living",
  "community-participation",
  "support-coordination",
  "community-nursing",
  "in-home-support",
];

// Cities with tailored page variants. Scalable across Australia.
export const LANDING_CITIES = {
  melbourne: {
    slug: "melbourne",
    name: "Melbourne",
    state: "VIC",
    nearby: ["Docklands", "Brunswick", "Richmond", "Box Hill", "Essendon", "Footscray"],
    phone: "1300 380 692",
    blurb: "Our Victoria team operates from Docklands, servicing participants across metropolitan Melbourne and regional VIC.",
    hub: true,
  },
  perth: {
    slug: "perth",
    name: "Perth",
    state: "WA",
    nearby: ["West Perth", "Perth CBD", "Joondalup", "Fremantle", "Rockingham"],
    phone: "1300 380 692",
    blurb: "Our Western Australia team is based in West Perth, servicing participants across the Perth metropolitan area and greater WA.",
    hub: true,
  },
  sydney: {
    slug: "sydney",
    name: "Sydney",
    state: "NSW",
    nearby: ["Sydney CBD", "Parramatta", "North Sydney", "Liverpool", "Penrith"],
    phone: "1300 380 692",
    blurb: "Sydney and NSW participants are supported through Alpha's national intake model — a named coordinator, structured onboarding and a vetted delivery partner network across Greater Sydney, the Central Coast, Hunter and Illawarra. Most supports can be activated within 2–4 weeks.",
  },
  brisbane: {
    slug: "brisbane",
    name: "Brisbane",
    state: "QLD",
    nearby: ["Brisbane CBD", "Logan", "Ipswich", "Sunshine Coast", "Gold Coast"],
    phone: "1300 380 692",
    blurb: "Across South East Queensland — from Brisbane through Logan, Ipswich and the Gold/Sunshine Coasts — Alpha onboards participants through our national coordination model and warm-referral delivery partners, with local capacity checked on every enquiry.",
  },
  adelaide: {
    slug: "adelaide",
    name: "Adelaide",
    state: "SA",
    nearby: ["Adelaide CBD", "Norwood", "Port Adelaide", "Glenelg", "Salisbury"],
    phone: "1300 380 692",
    blurb: "South Australian participants are supported across Greater Adelaide and regional SA through Alpha's national model. Intake confirms partner capacity, runs a structured discovery call and has you service-ready with a named coordinator and clear documentation.",
  },
  canberra: {
    slug: "canberra",
    name: "Canberra",
    state: "ACT",
    nearby: ["Canberra CBD", "Belconnen", "Tuggeranong", "Gungahlin", "Woden"],
    phone: "1300 380 692",
    blurb: "ACT participants access the same Alpha process — warm intake, coordinated clinical oversight and vetted delivery partners across Canberra and Queanbeyan. Supports are typically activated within 3–4 weeks of referral.",
  },
  hobart: {
    slug: "hobart",
    name: "Hobart",
    state: "TAS",
    nearby: ["Hobart CBD", "Sandy Bay", "Glenorchy", "Launceston"],
    phone: "1300 380 692",
    blurb: "Tasmania is serviced nationally by Alpha — from Hobart through Launceston and the North-West. Intake confirms local partner capacity, plans transport and clinical collaboration, and keeps families in the loop every step of the way.",
  },
  darwin: {
    slug: "darwin",
    name: "Darwin",
    state: "NT",
    nearby: ["Darwin CBD", "Palmerston", "Casuarina", "Alice Springs"],
    phone: "1300 380 692",
    blurb: "Northern Territory participants — from the Top End to Alice Springs — are supported through Alpha's national intake. We confirm regional capacity, map culturally-safe options with local partners and keep paperwork minimal and plain-English.",
  },
};

// Rich, landing-page-specific content by slug. Used in addition to the core
// data in services.js (which is still the single source of truth for the
// short intro, inclusions, etc.). This file adds the extra SEO/landing polish.
export const LANDING_CONTENT = {
  sil: {
    tagline: "Supported Independent Living, delivered properly.",
    heroImage: "/homes/coburg/1.jpg",
    heroBlurb:
      "24/7 in-home SIL support with clinical oversight and a consistent team. Registered with the NDIS and available across Melbourne and Perth — including forensic-overlay placements.",
    metaTitle: "Supported Independent Living (SIL) | NDIS | Melbourne & Perth",
    metaDescription:
      "Registered NDIS provider delivering 24/7 Supported Independent Living across Melbourne and Perth. Forensic overlay, high-intensity & behavioural supports.",
    keywords: [
      "NDIS SIL provider",
      "supported independent living Melbourne",
      "SIL Perth",
      "24/7 SIL",
      "forensic SIL",
    ],
    benefits: [
      ["24/7 active support", "Overnight and sleepover options. Familiar team, every shift."],
      ["Consistent rostering", "We match workers to values and preferences — not just availability."],
      ["Clinical oversight", "Nurses, PBS practitioners and allied health built into the model."],
      ["Forensic overlay", "Specifically-trained staff. Trauma-informed, restorative practice."],
    ],
    outcomes: [
      "More independence in the home",
      "Reduced hospital readmissions",
      "Clearer goals, documented progress",
      "Stable tenancies and healthier routines",
    ],
  },
  sda: {
    tagline: "Specialist Disability Accommodation you can move into with confidence.",
    heroImage: "/homes/aintree/1.jpg",
    heroBlurb:
      "Purpose-built SDA homes across all four design categories, with Alpha's 24/7 SIL where it's funded — in Melbourne and Perth.",
    metaTitle: "SDA Housing | Specialist Disability Accommodation | Melbourne & Perth",
    metaDescription:
      "Accessible NDIS SDA homes across Melbourne and Perth — Fully Accessible, High Physical Support, Improved Liveability and Robust categories. Tours available.",
    keywords: [
      "SDA housing Melbourne",
      "SDA Perth",
      "NDIS SDA provider",
      "high physical support SDA",
      "SDA vacancies",
    ],
    benefits: [
      ["All four SDA categories", "Fully Accessible, High Physical Support, Improved Liveability and Robust."],
      ["Warm, not institutional", "Homes that feel like homes — with real interiors and natural light."],
      ["Co-located SIL", "Move in with 24/7 supports where funded, or bring your preferred provider."],
      ["Tour + transition support", "Virtual and in-person tours. Coordinated handover from your current setting."],
    ],
    outcomes: [
      "A home that fits your needs today and tomorrow",
      "Reliable 24/7 support where funded",
      "Reduced barriers, more independence",
      "Clear pricing and tenancy terms",
    ],
  },
  "daily-living": {
    tagline: "Assistance with Daily Living (ADL) that actually fits your week.",
    heroImage: tile("daily-living", "Assistance with Daily Living"),
    heroBlurb:
      "Person-centred ADL support at home — personal care, cooking, cleaning, routines — delivered by a consistent team.",
    metaTitle: "ADL & Daily Living NDIS Support | Melbourne & Perth",
    metaDescription:
      "Assistance with Daily Living (ADL) from a registered NDIS provider in Melbourne and Perth. Consistent support workers, flexible rosters.",
    keywords: [
      "NDIS daily living support",
      "ADL Melbourne",
      "assistance with daily living Perth",
      "personal care NDIS",
      "in-home support",
    ],
    benefits: [
      ["Matched workers", "We match on values, culture and preferences — not random rostering."],
      ["Flexible hours", "Weekday, evening, weekend. Short or long shifts to fit your plan."],
      ["Skill-building built in", "Support that grows capability, not dependency."],
      ["Plain-English plans", "Weekly plans you understand, reviewed with you."],
    ],
    outcomes: [
      "Calmer weeks, fewer cancellations",
      "Real progress on your NDIS goals",
      "Respite relief for family carers",
      "Measurable daily-living independence gains",
    ],
  },
  "community-participation": {
    tagline: "Community Participation that feels like real life.",
    heroImage: tile("community-participation", "Community Participation"),
    heroBlurb:
      "1:1 and small-group supports to help you do more of what you enjoy — confidently, safely and consistently.",
    metaTitle: "NDIS Community Participation | Melbourne & Perth",
    metaDescription:
      "Community Participation supports with Alpha Community Care. 1:1 and group outings, skill-building and interest-led programs across Melbourne and Perth.",
    keywords: [
      "community participation NDIS",
      "social support NDIS",
      "group programs disability",
      "1:1 support Melbourne",
    ],
    benefits: [
      ["Interest-led", "Programs shaped by what you actually enjoy."],
      ["Inclusive & culturally safe", "LGBTQIA+ friendly, culturally-responsive options."],
      ["Consistent workers", "Familiar people who know your routines and interests."],
      ["Transport included", "Support extends to getting you there and back safely."],
    ],
    outcomes: [
      "New routines and friendships",
      "Increased confidence in public",
      "Stronger community connection",
      "Skills that transfer beyond programs",
    ],
  },
  "support-coordination": {
    tagline: "Support Coordination that earns its budget.",
    heroImage: tile("support-coordination", "Support Coordination"),
    heroBlurb:
      "A named coordinator who understands your plan, activates your supports fast and communicates clearly — with you and your circle.",
    metaTitle: "NDIS Support Coordination | Melbourne & Perth",
    metaDescription:
      "Responsive, plain-English NDIS Support Coordination. Named coordinators, warm referrals, clear documentation. Registered NDIS provider.",
    keywords: [
      "NDIS support coordination",
      "support coordinator Melbourne",
      "support coordinator Perth",
      "plan management NDIS",
    ],
    benefits: [
      ["Named coordinator", "No bouncing between people. One contact who knows your plan."],
      ["Warm referrals", "Only vetted, reliable providers we'd use ourselves."],
      ["Clear documentation", "Goals, progress and budgets — all written down and accessible."],
      ["Escalation pathway", "Crisis and complex scenarios handled with urgency."],
    ],
    outcomes: [
      "Plan activated in weeks, not months",
      "Budgets working in your favour",
      "Fewer gaps between providers",
      "Better preparation for plan reviews",
    ],
  },
  "community-nursing": {
    tagline: "Community Nursing, delivered at home.",
    heroImage: tile("community-nursing", "Community Nursing · Registered nurses"),
    heroBlurb:
      "Registered and enrolled nurses providing complex clinical support in your own home — wounds, medication, continence, tracheostomy and more.",
    metaTitle: "NDIS Community Nursing Care | Melbourne & Perth",
    metaDescription:
      "NDIS-funded Community Nursing Care from registered nurses. Wound care, medication, continence, enteral and tracheostomy support. Melbourne & Perth.",
    keywords: [
      "NDIS community nursing",
      "high intensity supports nursing",
      "wound care NDIS",
      "tracheostomy support Melbourne",
    ],
    benefits: [
      ["Registered nurses", "RNs and ENs under clinical governance."],
      ["High-intensity ready", "Tracheostomy, enteral, medication, complex wound care."],
      ["Continuity of care", "Regular clinicians. Consistent documentation."],
      ["GP/allied health liaison", "We coordinate with your broader clinical team."],
    ],
    outcomes: [
      "Fewer hospital presentations",
      "Safer medication management",
      "Improved wound healing",
      "Confident, supported families",
    ],
  },
  "in-home-support": {
    tagline: "In-home support, shaped around your life.",
    heroImage: tile("in-home-support", "In-Home Support"),
    heroBlurb:
      "Flexible one-on-one supports at home — built around your routines, your home, and your plan.",
    metaTitle: "NDIS In-Home Support | Melbourne & Perth",
    metaDescription:
      "Flexible NDIS in-home support across Melbourne and Perth. Personal care, domestic support, transport and respite from a registered NDIS provider.",
    keywords: [
      "in-home support NDIS",
      "home care NDIS Melbourne",
      "respite support",
      "personal care at home",
    ],
    benefits: [
      ["Flexible shifts", "From a couple of hours a week through to high-intensity daily."],
      ["Consistent workers", "Familiar faces. No roster roulette."],
      ["Respite options", "Planned respite for primary carers."],
      ["Transport included", "Medical appointments, community access, errands."],
    ],
    outcomes: [
      "Fewer gaps and missed shifts",
      "Better health and medication adherence",
      "Relief for family carers",
      "More time outside the home when you want it",
    ],
  },
};
